import React, { useState, useEffect } from "react";
import { PackagePlus, CheckCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import RuleAction from "../../actions/RuleAction";
import {
  connectApp,
  openLink,
  getAppLink,
  getCurrentFacebookMe,
  getCurrentFacebookPages,
  getCurrentInstagramMe,
  getCurrentTwitterMe,
  getCurrentLinkedInMe,
  getCurrentThreadsMe,
} from "../../api/AppLinkApi";
import LayoutAction from "../../actions/LayoutAction";
import { connectRedirectUrlAiContent } from "../../Navigator";
import { PLATFORM_ICONS } from "../constants/platformIcons";
import { selectAiSettingPlain } from "../selectors/aiContentSelectors";
import ProfileAvatar from "../../rules/components/ProfileAvatar";

export interface Platform {
  id: string;
  name: string;
  icon: React.ComponentType<any>;
  connected: boolean;
  selected: boolean;
}

interface PlatformSelectionProps {
  view: string;
  initialPlatforms?: Platform[];
  onPlatformsChange?: (platforms: Platform[]) => void;
  onConnectPlatform?: (platformId: string) => Promise<void>;
}

const PlatformSelection: React.FC<PlatformSelectionProps> = ({
  view,
  initialPlatforms,
  onPlatformsChange,
  onConnectPlatform,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { projectId } = useParams<{ projectId: string }>();

  const appLinks = useSelector((state: any) => state.ruleReducer.get("appLinks"));
  const aiSetting = useSelector(selectAiSettingPlain);
  const platformProfiles = useSelector((state: any) => state.ruleReducer.get("platformProfiles"));
  const [loadingProfiles, setLoadingProfiles] = useState<Record<string, boolean>>({});

  const platformAppMap: Record<string, string> = {
    facebook: "facebook",
    twitter: "twitter",
    instagram: "instagram",
    linkedin: "linkedin",
    threads: "threads",
  };

  const isPlatformConnected = (platformId: string): boolean => {
    if (!appLinks) return false;

    const appType = platformAppMap[platformId];
    const app = appLinks.toJS().find((item: any) => item.app === appType);

    return app?.connected || false;
  };

  const isPlatformSelected = (platformId: string): boolean => {
    if (!aiSetting) return false;

    const platformFlags: Record<string, boolean> = {
      facebook: !!aiSetting.facebook,
      twitter: !!aiSetting.twitter,
      instagram: !!aiSetting.instagram,
      linkedin: !!aiSetting.linkedin,
      threads: !!aiSetting.threads,
    };

    return platformFlags[platformId] || false;
  };

  const getDefaultPlatforms = (): Platform[] => {
    const basePlatforms = [
      {
        id: "instagram",
        name: t("Instagram"),
        icon: PLATFORM_ICONS.instagram,
        connected: false,
        selected: true,
      },
      {
        id: "facebook",
        name: t("Facebook"),
        icon: PLATFORM_ICONS.facebook,
        connected: false,
        selected: false,
      },
      {
        id: "linkedin",
        name: t("LinkedIn"),
        icon: PLATFORM_ICONS.linkedin,
        connected: false,
        selected: false,
      },
      {
        id: "twitter",
        name: t("X (Twitter)"),
        icon: PLATFORM_ICONS.twitter,
        connected: false,
        selected: false,
      },
      {
        id: "threads",
        name: t("Threads"),
        icon: PLATFORM_ICONS.threads,
        connected: false,
        selected: true,
      },
    ];

    return basePlatforms.map((platform) => {
      const connected = isPlatformConnected(platform.id);
      const selectedInSetting = isPlatformSelected(platform.id);
      return {
        ...platform,
        connected,
        selected: connected && selectedInSetting,
      };
    });
  };

  const [platforms, setPlatforms] = useState<Platform[]>(initialPlatforms || getDefaultPlatforms());

  const handlePlatformToggle = (platformId: string) => {
    const isConnected = isPlatformConnected(platformId);
    if (!isConnected) {
      dispatch(
        LayoutAction.setNotification({
          title: t("Platform Not Connected"),
          type: "warning",
          message: t("Please connect this platform first before selecting it."),
        })
      );
      return;
    }

    setPlatforms((prev) =>
      prev.map((platform) =>
        platform.id === platformId ? { ...platform, selected: !platform.selected } : platform
      )
    );
  };

  const handleConnectPlatform = async (platformId: string) => {
    if (!projectId) return;

    const appType = platformAppMap[platformId];
    if (!appType) return;

    if (onConnectPlatform) {
      await onConnectPlatform(platformId);
    }

    try {
      const postUrl = connectRedirectUrlAiContent(projectId, platformId, view);
      const newAppData = await connectApp(projectId, appType, postUrl);
      const { redirectUrl = "" } = newAppData;

      if (redirectUrl !== "" && !isPlatformConnected(platformId)) {
        openLink(redirectUrl);
      } else {
        dispatch(
          LayoutAction.setNotification({
            title: t("App"),
            type: "success",
            message: t("App enabled successfully!"),
          })
        );
        dispatch(RuleAction.getAppLinks());
      }
    } catch (error) {
      console.error("Error connecting platform:", error);
      dispatch(
        LayoutAction.setNotification({
          title: t("App"),
          type: "error",
          message: t("App enable failed!"),
        })
      );
    }
  };

  useEffect(() => {
    dispatch(RuleAction.getAppLinks());
  }, [dispatch]);

  useEffect(() => {
    if (appLinks || aiSetting) {
      setPlatforms((prev) =>
        prev.map((platform) => {
          const connected = isPlatformConnected(platform.id);
          const selectedInSetting = isPlatformSelected(platform.id);
          return {
            ...platform,
            connected,
            selected: connected && selectedInSetting,
          };
        })
      );
    }
  }, [appLinks, aiSetting]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (initialPlatforms) {
      setPlatforms(initialPlatforms);
    }
  }, [initialPlatforms]);

  useEffect(() => {
    if (onPlatformsChange) {
      onPlatformsChange(platforms);
    }
  }, [platforms, onPlatformsChange]);

  const platformApiMap: Record<string, () => Promise<any>> = {
    facebook: getCurrentFacebookMe,
    instagram: getCurrentInstagramMe,
    twitter: getCurrentTwitterMe,
    linkedin: getCurrentLinkedInMe,
    threads: getCurrentThreadsMe,
  };

  const transformProfileData = (platformId: string, data: any) => {
    if (!data) return null;

    switch (platformId) {
      case "facebook":
        return {
          picture: data?.pictureUrl,
          name: data?.name,
          username: data?.email,
        };
      case "instagram":
        return {
          picture: data?.profilePictureUrl,
          name: data?.name,
          username: data?.username,
        };
      case "twitter":
        return {
          picture: data?.profile_image_url,
          name: data?.name,
          username: data?.username,
        };
      case "linkedin":
        return {
          picture: data?.picture,
          name: `${data?.given_name || ""} ${data?.family_name || ""}`.trim(),
          username: data?.email,
        };
      case "threads":
        return {
          picture: data?.threads_profile_picture_url,
          name: data?.name,
          username: data?.username,
        };
      default:
        return null;
    }
  };

  const getPlatformProfile = (platformId: string, appName: string) => {
    const reduxProfile = platformProfiles?.get?.(appName)?.toJS?.();
    return reduxProfile || null;
  };

  const getFacebookPageProfile = () => {
    if (platformProfiles) {
      const pageProfile = platformProfiles?.get?.("facebook-page")?.toJS?.();
      return pageProfile || null;
    }
    return null;
  };

  const loadPlatformProfile = async (platformId: string, appName: string) => {
    if (loadingProfiles[platformId]) return;

    const apiCall = platformApiMap[appName];
    if (!apiCall) return;

    setLoadingProfiles((prev) => ({ ...prev, [platformId]: true }));

    try {
      // For Facebook, load both user and default Page (mirror AppProfilePreview behavior)
      if (appName === "facebook") {
        const [me, appLink] = await Promise.all([getCurrentFacebookMe(), getAppLink("facebook")]);

        const userProfile = transformProfileData("facebook", me);
        if (userProfile) {
          dispatch(
            RuleAction.setPlatformProfile({
              platformId: "facebook",
              profile: userProfile,
            })
          );
        }

        const pageId = appLink?.data?.page_id || "";
        if (pageId) {
          const pages = await getCurrentFacebookPages();
          const page = (pages || []).find((p: any) => String(p?.id) === String(pageId)) || null;

          if (page) {
            dispatch(
              RuleAction.setPlatformProfile({
                platformId: "facebook-page",
                profile: {
                  id: page.id,
                  name: page.name,
                  picture: page.pictureUrl || "",
                  username: page.id,
                },
              })
            );
          }
        }
        return;
      }

      const data = await apiCall();
      const profile = transformProfileData(appName, data);

      if (profile) {
        dispatch(
          RuleAction.setPlatformProfile({
            platformId: appName,
            profile,
          })
        );
      }
    } catch (err) {
      console.error(`Failed to load profile for ${platformId}:`, err);
    } finally {
      setLoadingProfiles((prev) => ({ ...prev, [platformId]: false }));
    }
  };

  const getPlatformIconStyle = (platformId: string, isConnected: boolean) => {
    const baseStyle = "w-8 h-8 rounded-full flex items-center justify-center";
    const opacityStyle = !isConnected ? "opacity-50" : "";

    const colorStyle =
      platformId === "instagram"
        ? "bg-gradient-to-r from-yellow-400 via-red-500 to-purple-600"
        : platformId === "facebook"
          ? "bg-blue-600"
          : platformId === "linkedin"
            ? "bg-blue-700"
            : platformId === "twitter"
              ? "bg-black"
              : "bg-gray-800";

    return `${baseStyle} ${colorStyle} ${opacityStyle}`;
  };

  // Load profiles for connected platforms
  useEffect(() => {
    if (!appLinks) return;

    platforms.forEach((platform) => {
      const isConnected = isPlatformConnected(platform.id);
      const appName = platformAppMap[platform.id];

      if (isConnected && appName) {
        const existingProfile = platformProfiles?.get?.(appName);
        if (!existingProfile && !loadingProfiles[platform.id]) {
          loadPlatformProfile(platform.id, appName);
        }
      }
    });
  }, [appLinks]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-3">
        {t("Which platform would you like to post on?")}
      </label>
      <div>
        {platforms.map((platform) => {
          const IconComponent = platform.icon;
          const isConnected = isPlatformConnected(platform.id);
          const appName = platformAppMap[platform.id];
          const profile = appName ? getPlatformProfile(platform.id, appName) : null;
          const facebookPageProfile = platform.id === "facebook" ? getFacebookPageProfile() : null;
          const loading = loadingProfiles[platform.id] || false;

          const PlatformIconBadge = () => {
            const badgeSize = "w-4 h-4";
            const badgeStyle = getPlatformIconStyle(platform.id, true).replace(
              "w-8 h-8",
              badgeSize
            );
            return (
              <div className={badgeStyle}>
                <IconComponent className="w-2.5 h-2.5 text-white" />
              </div>
            );
          };

          return (
            <div
              key={platform.id}
              className="flex items-center justify-between hover:bg-gray-50 transition-all py-1.5"
            >
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={platform.selected}
                  onChange={() => handlePlatformToggle(platform.id)}
                  disabled={!isConnected}
                  className={`w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 ${
                    !isConnected ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                />
                {isConnected && profile ? (
                  <ProfileAvatar
                    picture={profile.picture}
                    name={profile.name}
                    size="sm"
                    platformBadge={<PlatformIconBadge />}
                  />
                ) : loading ? (
                  <div className="relative w-8 h-8 flex-shrink-0 overflow-hidden rounded-full bg-gray-200">
                    <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-white/70 to-transparent" />
                  </div>
                ) : (
                  <div className={getPlatformIconStyle(platform.id, isConnected)}>
                    <IconComponent className="w-4 h-4 text-white" />
                  </div>
                )}
                <div className="flex flex-col">
                  <span
                    className={`text-sm font-medium ${
                      isConnected ? "text-gray-900" : "text-gray-500"
                    }`}
                  >
                    {platform.name}
                  </span>
                  {isConnected && profile?.username && (
                    <span className="text-xs text-gray-500 font-mono">
                      {profile.username.startsWith("@") ? profile.username : `@${profile.username}`}
                    </span>
                  )}
                  {isConnected && facebookPageProfile && (
                    <span className="text-xs font-semibold text-gray-500 truncate">
                      Page: {facebookPageProfile.name}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {isConnected ? (
                  <div className="w-6 h-6 flex items-center justify-center">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                  </div>
                ) : (
                  <button
                    onClick={() => handleConnectPlatform(platform.id)}
                    className="flex items-center gap-1 px-3 py-1 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-100 transition-colors"
                  >
                    <PackagePlus className="w-3 h-3" />
                    {t("Connect")}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PlatformSelection;
